// React imports
import React, { FunctionComponent, useState, useEffect, useRef } from 'react';

// Third-party imports
import clsx from 'clsx';
import { faTrash } from '@fortawesome/free-solid-svg-icons';

// UI Components
import { PriceField } from '../../../UI/V3/Forms/PriceField';
import { NumberField } from '../../../UI/V3/Forms/NumberField';
import { Label } from '../../../UI/V3/Forms/Label/Label';
import Button from '../../../UI/V3/Button/Button';

// Styles
import styles from './TieredValueTypeSelector.module.scss';

export type ValueType = 'amount' | 'percentage';

export interface TieredValueTypeSelectorRowValue {
    type: ValueType;
    value: number;
    label: string;
}

export interface TieredValueTypeSelectorProps {
    id?: string;
    className?: string;
    required?: boolean;
    values?: TieredValueTypeSelectorRowValue[];
    defaultValue?: string;
    max?: number;
    placeholder?: string;
    disabled?: boolean;
    errors?: (string | null)[];
    onChange?: (values: TieredValueTypeSelectorRowValue[]) => void;
    onKeyPress?: (event: React.KeyboardEvent) => void;
    rowLabel: string;
    addButtonLabel?: string;
    showAddButton?: boolean;
    initialRowCount: number;
}

interface RowData {
    label: string;
    value: number;
    error: string | null;
    isRemovable: boolean;
}

// Helper function to generate ordinal numbers (2nd, 3rd, 4th, etc.)
const getOrdinal = (n: number): string => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
};

export const TieredValueTypeSelector: FunctionComponent<TieredValueTypeSelectorProps> = ({
    id,
    className,
    required = false,
    values,
    defaultValue,
    max,
    placeholder = '0.00',
    disabled = false,
    errors = [],
    onChange,
    onKeyPress,
    rowLabel,
    addButtonLabel = 'Add',
    showAddButton = false,
    initialRowCount
}) => {
    const [selectedType, setSelectedType] = useState<ValueType>(values?.[0]?.type || 'amount');
    const [rows, setRows] = useState<RowData[]>(() => {
        const initialRows: RowData[] = [];
        for (let i = 0; i < initialRowCount; i++) {
            // Start from 2 (2nd, 3rd, etc.)
            const ordinal = getOrdinal(i + 2);
            initialRows.push({
                label: `${ordinal} ${rowLabel}`,
                value: values?.[i]?.value || 0,
                error: errors[i] || null,
                isRemovable: false
            });
        }
        return initialRows;
    });
    const [defaultState, setDefaultState] = useState<string | undefined>(defaultValue);
    const isInitialMount = useRef(true);
    const isSyncingFromParent = useRef(false);

    // Sync with external value changes
    useEffect(() => {
        if (values && values.length > 0) {
            isSyncingFromParent.current = true;
            setSelectedType(values[0].type);
            setRows(prevRows => {
                return prevRows.map((row, index) => ({
                    ...row,
                    value: values[index]?.value ?? row.value
                }));
            });
            // Reset flag after state updates are queued
            setTimeout(() => {
                isSyncingFromParent.current = false;
            }, 0);
        }
    }, [values]);

    // Sync errors with rows
    useEffect(() => {
        isSyncingFromParent.current = true;
        setRows(prevRows => {
            return prevRows.map((row, index) => ({
                ...row,
                error: errors[index] || null
            }));
        });
        setTimeout(() => {
            isSyncingFromParent.current = false;
        }, 0);
    }, [errors]);

    // Handle changes with debounce effect
    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
            return;
        }

        // Skip onChange if we're syncing from parent
        if (isSyncingFromParent.current) {
            return;
        }

        const timer = setTimeout(() => {
            setDefaultState(undefined);
            const result: TieredValueTypeSelectorRowValue[] = rows.map(row => ({
                type: selectedType,
                value: row.value,
                label: row.label
            }));
            onChange?.(result);
        }, 300);

        return () => clearTimeout(timer);
    }, [rows, selectedType, onChange]);

    // Handle external defaultValue changes
    useEffect(() => {
        if (defaultValue) {
            setDefaultState(defaultValue);
        }
    }, [defaultValue]);

    const handleTypeChange = (type: ValueType) => {
        setSelectedType(type);
        setRows(prevRows =>
            prevRows.map(row => ({
                ...row,
                value: 0
            }))
        );
        setDefaultState(type === 'amount' ? '0.00' : '0');

        // Immediately notify parent of type change
        const result: TieredValueTypeSelectorRowValue[] = rows.map(row => ({
            type,
            value: 0,
            label: row.label
        }));
        onChange?.(result);
    };

    const handleValueChange = (index: number, newValue?: number) => {
        const value = newValue || 0;

        // For percentage type, don't allow values > 100
        if (selectedType === 'percentage' && value > 100) {
            return;
        }

        // For amount type, cap at max if provided
        if (selectedType === 'amount' && max !== undefined && value > max) {
            const cappedValue = max;
            setRows(prevRows => {
                const newRows = [...prevRows];
                newRows[index].value = cappedValue;
                return newRows;
            });
            return;
        }

        setRows(prevRows => {
            const newRows = [...prevRows];
            newRows[index].value = value;
            return newRows;
        });
    };

    const handleKeyPress = (event: React.KeyboardEvent) => {
        // Skip key press validation on mobile to avoid context menu issues
        if ('ontouchstart' in window) {
            return;
        }

        onKeyPress?.(event);

        if (event.defaultPrevented) {
            return;
        }

        // For percentage type, prevent entering values > 100
        if (selectedType === 'percentage') {
            const target = event.currentTarget as HTMLInputElement;
            const currentValue = target?.value || '';
            const newValue = currentValue + event.key;

            if (newValue && !isNaN(Number(newValue))) {
                if (Number(newValue) > 100) {
                    event.preventDefault();
                }
            }
        }
    };

    const handleAddRow = () => {
        const nextIndex = rows.length;
        // Continue the ordinal sequence (e.g., if we have 4 rows, next is 6th)
        const ordinal = getOrdinal(nextIndex + 2);
        const newLabel = `${ordinal} ${rowLabel}`;
        setRows(prevRows => [
            ...prevRows,
            {
                label: newLabel,
                value: 0,
                error: null,
                isRemovable: true
            }
        ]);
    };

    const handleRemoveRow = (index: number) => {
        setRows(prevRows => prevRows.filter((_, i) => i !== index));
    };

    const amountButtonClasses = clsx(styles.typeButton, {
        [styles.selected]: selectedType === 'amount'
    });

    const percentageButtonClasses = clsx(styles.typeButton, {
        [styles.selected]: selectedType === 'percentage'
    });

    const containerClasses = clsx(styles.container, className);

    return (
        <div
            id={id}
            className={containerClasses}
        >
            <div className={styles.typeSelectorContainer}>
                <div className={styles.typeSelector}>
                    <button
                        type="button"
                        className={amountButtonClasses}
                        onClick={() => handleTypeChange('amount')}
                        disabled={disabled}
                    >
                        <span
                            className={clsx(styles.typeText, {
                                [styles.selectedText]: selectedType === 'amount'
                            })}
                        >
                            $
                        </span>
                    </button>
                    <button
                        type="button"
                        className={percentageButtonClasses}
                        onClick={() => handleTypeChange('percentage')}
                        disabled={disabled}
                    >
                        <span
                            className={clsx(styles.typeText, {
                                [styles.selectedText]: selectedType === 'percentage'
                            })}
                        >
                            %
                        </span>
                    </button>
                </div>
            </div>

            <div className={styles.rowsContainer}>
                {rows.map((row, index) => (
                    <div
                        key={index}
                        className={styles.row}
                    >
                        <div className={styles.rowContent}>
                            <div className={styles.labelContainer}>
                                <Label required={required}>{row.label}</Label>
                            </div>

                            <div className={styles.inputContainer}>
                                {selectedType === 'percentage' ? (
                                    <NumberField
                                        value={row.value}
                                        default={defaultState}
                                        placeholder={placeholder}
                                        scale={0}
                                        max={max}
                                        disabled={disabled}
                                        onChange={value => handleValueChange(index, value)}
                                        onKeyPress={handleKeyPress}
                                        endIcon={<span className={styles.percentageSymbol}>%</span>}
                                        className={styles.field}
                                    />
                                ) : (
                                    <PriceField
                                        value={row.value}
                                        default={defaultState}
                                        placeholder={placeholder}
                                        scale={2}
                                        precision={8}
                                        max={max}
                                        disabled={disabled}
                                        onChange={value => handleValueChange(index, value)}
                                        onKeyPress={handleKeyPress}
                                        className={styles.field}
                                    />
                                )}
                            </div>

                            {row.error && (
                                <div className={styles.errorContainer}>
                                    <span className={styles.errorMessage}>{row.error}</span>
                                </div>
                            )}

                            {row.isRemovable && (
                                <div className={styles.removeButtonContainer}>
                                    <Button
                                        label=""
                                        variant="danger"
                                        size="small"
                                        onlyIcon
                                        leftIcon={faTrash}
                                        onClick={() => handleRemoveRow(index)}
                                        disabled={disabled}
                                        className={styles.removeButton}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {showAddButton && (
                <div className={styles.addButtonContainer}>
                    <Button
                        label={addButtonLabel}
                        variant="black"
                        size="medium"
                        onClick={handleAddRow}
                        disabled={disabled}
                        fullWidth
                    />
                </div>
            )}
        </div>
    );
};

export default TieredValueTypeSelector;
